/*
--------------------------------------------------------------------------------
-------------------------------> EDITOR STATE <---------------------------------
--------------------------------------------------------------------------------

  Single source of truth for "which image does this line item use, and how is it
  cropped?".

  Order of authority, most authoritative first:

    1. The Shopify cart (/cart.js). Server side, survives a cleared browser, a
       different device, an expired session. This is the truth.
    2. localStorage. A convenience CACHE only, for an upload that has not yet
       reached the cart.
    3. The URL hash. Carries identifiers only (which image, which cart line) --
       never the crop itself.

  Previously the crop lived ONLY in localStorage, keyed by filename, and the
  deep-link restore path read it but never wrote it back into the hidden form
  field. That is the main reason line items could reach checkout with an empty
  _prodigi_crop: anyone returning to the product page from the basket, or simply
  refreshing, re-submitted the form with a blank crop.

  Crop coordinate space
  ---------------------
  Crop coordinates are expressed in the pixel space of the 1000px "web" image
  (CR.constants.webPx), which is what Cropper's getData() returns and what the
  order pipeline expects. Do not mix in coordinates from the original or the
  preview.
*/

window.CR = window.CR || {};

CR.state = (function ($) {
  'use strict';

  var CROP_PREFIX = 'cr_crop_';
  var CACHE_INDEX = 'cr_crop_index';
  var CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // S3 lifecycle deletes at 84 days

  // ---------------------------------------------------------------------------
  // Storage. Wrapped because Safari private mode throws on setItem, and some
  // privacy extensions remove localStorage entirely.
  // ---------------------------------------------------------------------------

  function storage() {
    try {
      var s = window.localStorage;
      var probe = '__cr_probe__';
      s.setItem(probe, '1');
      s.removeItem(probe);
      return s;
    } catch (e) {
      return null;
    }
  }

  function storageGet(key) {
    var s = storage();
    if (!s) { return null; }
    try { return s.getItem(key); } catch (e) { return null; }
  }

  function storageSet(key, value) {
    var s = storage();
    if (!s) { return false; }
    try {
      s.setItem(key, value);
      return true;
    } catch (e) {
      // Most likely quota. Prune and try once more.
      pruneCache();
      try {
        s.setItem(key, value);
        return true;
      } catch (e2) {
        return false;
      }
    }
  }

  function storageRemove(key) {
    var s = storage();
    if (!s) { return; }
    try { s.removeItem(key); } catch (e) { /* nothing sensible to do */ }
  }

  // ---------------------------------------------------------------------------
  // Crop parsing and validation
  //
  // Everything that comes off the wire, out of storage or out of a form field is
  // untrusted. A crop is only usable if all four values are finite numbers and
  // the box has a real area. "0,0,0,0" and "NaN,NaN,NaN,NaN" have both been seen
  // in the wild and must be treated as absent, not as a valid crop.
  // ---------------------------------------------------------------------------

  function toFiniteNumber(value) {
    var n = typeof value === 'number' ? value : parseFloat(value);
    return isFinite(n) ? n : null;
  }

  function parseCrop(value) {
    if (!value && value !== 0) { return null; }

    var parts;

    if (typeof value === 'string') {
      parts = value.split(',');
      if (parts.length !== 4) { return null; }
      parts = {
        x: parts[0], y: parts[1], width: parts[2], height: parts[3]
      };
    } else if (typeof value === 'object') {
      parts = value;
    } else {
      return null;
    }

    var crop = {
      x: toFiniteNumber(parts.x),
      y: toFiniteNumber(parts.y),
      width: toFiniteNumber(parts.width),
      height: toFiniteNumber(parts.height)
    };

    if (crop.x === null || crop.y === null || crop.width === null || crop.height === null) {
      return null;
    }
    // A zero-area or negative box is not a crop.
    if (crop.width <= 0 || crop.height <= 0) { return null; }
    // Negative offsets mean the box started outside the image.
    if (crop.x < 0 || crop.y < 0) { return null; }

    return crop;
  }

  function serialiseCrop(crop) {
    var valid = parseCrop(crop);
    if (!valid) { return ''; }
    return [
      Math.round(valid.x),
      Math.round(valid.y),
      Math.round(valid.width),
      Math.round(valid.height)
    ].join(',');
  }

  // Keep a crop inside the bounds of the image it refers to. Rounding on the way
  // in and out of storage can push a box a pixel or two past the edge, which the
  // downstream crop then rejects.
  function clampCrop(crop, naturalWidth, naturalHeight) {
    var valid = parseCrop(crop);
    if (!valid) { return null; }
    if (!naturalWidth || !naturalHeight) { return valid; }

    var out = {
      x: Math.min(valid.x, naturalWidth - 1),
      y: Math.min(valid.y, naturalHeight - 1),
      width: valid.width,
      height: valid.height
    };
    out.width = Math.min(out.width, naturalWidth - out.x);
    out.height = Math.min(out.height, naturalHeight - out.y);

    if (out.width <= 0 || out.height <= 0) { return null; }
    return out;
  }

  // ---------------------------------------------------------------------------
  // localStorage cache
  //
  // The cache exists so an upload survives a refresh BEFORE it reaches the cart.
  // Once the item is in the cart, the cart wins. Entries carry a timestamp and
  // are pruned, because the old implementation wrote a key per image forever.
  // ---------------------------------------------------------------------------

  function cropKey(filename) {
    return CROP_PREFIX + filename;
  }

  function readIndex() {
    var raw = storageGet(CACHE_INDEX);
    if (!raw) { return {}; }
    try {
      var parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  function writeIndex(index) {
    try {
      storageSet(CACHE_INDEX, JSON.stringify(index));
    } catch (e) { /* cache only, safe to lose */ }
  }

  function pruneCache() {
    var index = readIndex();
    var now = new Date().getTime();
    var kept = {};
    var filename;

    for (filename in index) {
      if (!index.hasOwnProperty(filename)) { continue; }
      if (now - index[filename] > CACHE_MAX_AGE_MS) {
        storageRemove(cropKey(filename));
      } else {
        kept[filename] = index[filename];
      }
    }
    writeIndex(kept);
  }

  function cacheCrop(filename, crop) {
    if (!filename) { return false; }
    var serialised = serialiseCrop(crop);
    if (!serialised) { return false; }

    var ok = storageSet(cropKey(filename), serialised);
    if (ok) {
      var index = readIndex();
      index[filename] = new Date().getTime();
      writeIndex(index);
    }
    return ok;
  }

  function readCachedCrop(filename) {
    if (!filename) { return null; }
    return parseCrop(storageGet(cropKey(filename)));
  }

  function forgetCrop(filename) {
    if (!filename) { return; }
    storageRemove(cropKey(filename));
    var index = readIndex();
    if (index[filename]) {
      delete index[filename];
      writeIndex(index);
    }
  }

  // ---------------------------------------------------------------------------
  // URL hash
  //
  // Format: #img=<filename>&line=<cart line key>
  //
  // The bare "#filename.jpg" form is still understood, because links of that
  // shape exist in the wild (old basket pages, bookmarks, emailed links).
  // ---------------------------------------------------------------------------

  function parseHash(hash) {
    var out = { filename: '', line: '' };

    if (typeof hash !== 'string') { return out; }
    hash = hash.replace(/^#/, '');
    if (hash === '') { return out; }

    // Legacy: a bare filename, no key=value pairs.
    if (hash.indexOf('=') === -1) {
      out.filename = safeDecode(hash);
      return out;
    }

    var pairs = hash.split('&');
    for (var i = 0; i < pairs.length; i++) {
      var eq = pairs[i].indexOf('=');
      if (eq === -1) { continue; }
      var key = pairs[i].slice(0, eq);
      var value = safeDecode(pairs[i].slice(eq + 1));
      if (key === 'img') { out.filename = value; }
      if (key === 'line') { out.line = value; }
    }
    return out;
  }

  function safeDecode(value) {
    try {
      return decodeURIComponent(value);
    } catch (e) {
      // Malformed percent-encoding. Better a raw string than an exception that
      // takes the whole page down.
      return value;
    }
  }

  function buildHash(state) {
    if (!state || !state.filename) { return ''; }
    var hash = 'img=' + encodeURIComponent(state.filename);
    if (state.line) {
      hash += '&line=' + encodeURIComponent(state.line);
    }
    return hash;
  }

  function readHash() {
    return parseHash(window.location.hash);
  }

  // replaceState rather than location.hash so we do not add a history entry per
  // crop, and do not trigger a scroll jump to a non-existent anchor.
  function writeHash(state) {
    var hash = buildHash(state);

    if (window.history && window.history.replaceState) {
      var url = window.location.pathname + window.location.search + (hash ? '#' + hash : '');
      try {
        window.history.replaceState(window.history.state || {}, document.title, url);
        return;
      } catch (e) { /* fall through */ }
    }
    window.location.hash = hash;
  }

  // ---------------------------------------------------------------------------
  // Reading state out of the cart
  // ---------------------------------------------------------------------------

  var PROP_THUMBNAIL = '_prodigi_imagethumbnail';
  var PROP_ORIGINAL = '_prodigi_imageoriginal';
  var PROP_CROP = '_prodigi_crop';

  // Filename portion of a URL or S3 key, minus folder and query string.
  function filenameFromUrl(url) {
    if (!url) { return ''; }
    var withoutQuery = String(url).split('?')[0].split('#')[0];
    var match = withoutQuery.match(/[^\/]*$/);
    return match ? safeDecode(match[0]) : '';
  }

  function fromCartLine(item) {
    if (!item || !item.properties) { return null; }

    var thumbnail = item.properties[PROP_THUMBNAIL] || '';
    var original = item.properties[PROP_ORIGINAL] || '';
    var filename = filenameFromUrl(thumbnail) || filenameFromUrl(original);

    if (!filename) { return null; }

    return {
      filename: filename,
      thumbnail: thumbnail,
      original: original,
      crop: parseCrop(item.properties[PROP_CROP]),
      line: item.key || '',
      variantId: item.variant_id || item.id || null,
      quantity: item.quantity || 1,
      productId: item.product_id || null,
      source: 'cart'
    };
  }

  // Find the line this page is editing. The line key is exact and preferred.
  // Falling back to filename covers legacy links; falling back to product id
  // covers a link whose key went stale because the cart was edited elsewhere.
  function findCartLine(cart, criteria) {
    if (!cart || !cart.items || !cart.items.length) { return null; }
    criteria = criteria || {};

    var i, item;

    if (criteria.line) {
      for (i = 0; i < cart.items.length; i++) {
        if (cart.items[i].key === criteria.line) { return cart.items[i]; }
      }
    }

    if (criteria.filename) {
      for (i = 0; i < cart.items.length; i++) {
        item = cart.items[i];
        if (!item.properties) { continue; }
        var itemFilename = filenameFromUrl(item.properties[PROP_THUMBNAIL]) ||
                           filenameFromUrl(item.properties[PROP_ORIGINAL]);
        if (itemFilename && itemFilename === criteria.filename) { return item; }
      }
    }

    return null;
  }

  // ---------------------------------------------------------------------------
  // Hydration
  //
  // Resolves the page's starting state. Returns a jQuery promise for an object
  // shaped like fromCartLine(), or null when there is nothing to restore.
  //
  // jQuery Deferreds rather than native Promises: this theme runs jQuery 1.11
  // and we are not adding a browser requirement to fix a data bug.
  // ---------------------------------------------------------------------------

  function hydrate(options) {
    options = options || {};
    var deferred = $.Deferred();
    var hash = readHash();

    // Nothing in the URL: no image is being restored. A first-time visitor.
    if (!hash.filename && !hash.line) {
      deferred.resolve(null);
      return deferred.promise();
    }

    CR.cart.get()
      .done(function (cart) {
        var item = findCartLine(cart, {
          line: hash.line,
          filename: hash.filename
        });

        var state = item ? fromCartLine(item) : null;

        if (state) {
          // The cart had it. Authoritative, including the crop.
          deferred.resolve(state);
          return;
        }
        deferred.resolve(localState(hash, options));
      })
      .fail(function () {
        // Cart unreachable (offline, rate limited, storefront password page).
        // Fall back to what we have locally rather than losing the upload.
        deferred.resolve(localState(hash, options));
      });

    return deferred.promise();
  }

  // Build state from the hash plus the localStorage cache, for an upload that
  // has not reached the cart yet.
  function localState(hash, options) {
    if (!hash.filename) { return null; }
    options = options || {};

    var base = options.uploadPath || (CR.constants && CR.constants.uploadPath) || '';
    var previewFolder = (CR.constants && CR.constants.previewFolder) || 'preview/';
    var originalFolder = (CR.constants && CR.constants.originalFolder) || 'original/';
    var encoded = encodeURIComponent(hash.filename);

    return {
      filename: hash.filename,
      thumbnail: 'https:' + base + previewFolder + encoded,
      original: 'https:' + base + originalFolder + encoded,
      crop: readCachedCrop(hash.filename),
      line: hash.line || '',
      variantId: null,
      quantity: 1,
      productId: null,
      source: 'local'
    };
  }

  // ---------------------------------------------------------------------------
  // Form <-> state
  // ---------------------------------------------------------------------------

  function applyToForm(state) {
    if (!state) { return; }

    if (state.thumbnail) { CR.cache.$imagePropertyThumbnail.val(state.thumbnail); }
    if (state.original) { CR.cache.$imagePropertyOriginal.val(state.original); }

    // The crop field is set even when the crop is absent, so a stale value from
    // a previous image can never survive into a new line item.
    CR.cache.$imagePropertyCrop.val(serialiseCrop(state.crop));
  }

  function readForm() {
    return {
      thumbnail: $.trim(CR.cache.$imagePropertyThumbnail.val() || ''),
      original: $.trim(CR.cache.$imagePropertyOriginal.val() || ''),
      crop: parseCrop(CR.cache.$imagePropertyCrop.val())
    };
  }

  // What "ready to add to the basket" means. Every one of these is required by
  // the order pipeline, so all three are checked in one place rather than being
  // assumed at half a dozen call sites.
  function missingFields(state) {
    state = state || readForm();
    var missing = [];
    if (!state.thumbnail) { missing.push('thumbnail'); }
    if (!state.original) { missing.push('original'); }
    if (!parseCrop(state.crop)) { missing.push('crop'); }
    return missing;
  }

  function isComplete(state) {
    return missingFields(state).length === 0;
  }

  function clearForm() {
    CR.cache.$imagePropertyThumbnail.val('');
    CR.cache.$imagePropertyOriginal.val('');
    CR.cache.$imagePropertyCrop.val('');
  }

  return {
    // crop
    parseCrop: parseCrop,
    serialiseCrop: serialiseCrop,
    clampCrop: clampCrop,
    // cache
    cacheCrop: cacheCrop,
    readCachedCrop: readCachedCrop,
    forgetCrop: forgetCrop,
    pruneCache: pruneCache,
    // hash
    parseHash: parseHash,
    buildHash: buildHash,
    readHash: readHash,
    writeHash: writeHash,
    // cart
    fromCartLine: fromCartLine,
    findCartLine: findCartLine,
    filenameFromUrl: filenameFromUrl,
    // lifecycle
    hydrate: hydrate,
    localState: localState,
    // form
    applyToForm: applyToForm,
    readForm: readForm,
    missingFields: missingFields,
    isComplete: isComplete,
    clearForm: clearForm,
    // constants, exported for tests and for the templates
    PROP_THUMBNAIL: PROP_THUMBNAIL,
    PROP_ORIGINAL: PROP_ORIGINAL,
    PROP_CROP: PROP_CROP
  };
})(jQuery);
